/**
 * 应用根：启动探活 → 登录页 or 控制台外框 → 按路由渲染页面。
 *
 * 路由用 hash（`#/payments`），不引 router 库：这套控制台只有十几个平铺页面，
 * 没有嵌套路由和路径参数（详情一律走弹窗），一个 hash 监听就够了。
 *
 * **切组织后要校验当前路由是否还看得见** —— 从平台切到商户身份时，
 * 「通道账户」这类页面就不再属于可见集合了。不校验的话会停在一个空白页上，
 * 或者更糟：发一串注定 403 的请求。
 */

import { useEffect, useState } from 'react';
import { useBootstrap, SessionProvider } from './lib/session.tsx';
import { visibleNav, defaultPath } from './lib/nav.ts';
import { Shell } from './components/shell.tsx';
import { Loading } from './components/ui.tsx';
import { LoginPage } from './pages/login.tsx';
import { OverviewPage } from './pages/overview.tsx';
import { PaymentsPage } from './pages/payments.tsx';
import { RefundsPage } from './pages/refunds.tsx';
import { DisputesPage } from './pages/disputes.tsx';
import { InvoicesPage } from './pages/invoices.tsx';
import { PaymentLinksPage } from './pages/payment-links.tsx';
import { OrgsPage } from './pages/orgs.tsx';
import { ChannelsPage } from './pages/channels.tsx';
import { GrantsPage } from './pages/grants.tsx';
import { BindingsPage } from './pages/bindings.tsx';
import { FeesPage } from './pages/fees.tsx';
import { LedgerPage } from './pages/ledger.tsx';
import { SettlementsPage } from './pages/settlements.tsx';
import { PayoutsPage } from './pages/payouts.tsx';
import { MembersPage } from './pages/members.tsx';
import { CredentialsPage } from './pages/credentials.tsx';
import { WebhookPage } from './pages/webhook.tsx';
import { ChannelGroupsPage } from './pages/channel-groups.tsx';
import { ProfilePage } from './pages/profile.tsx';

const PAGES: Readonly<Record<string, () => React.JSX.Element>> = {
  overview: OverviewPage,
  payments: PaymentsPage,
  refunds: RefundsPage,
  disputes: DisputesPage,
  invoices: InvoicesPage,
  payment_links: PaymentLinksPage,
  orgs: OrgsPage,
  channels: ChannelsPage,
  channel_groups: ChannelGroupsPage,
  grants: GrantsPage,
  bindings: BindingsPage,
  fees: FeesPage,
  ledger: LedgerPage,
  settlements: SettlementsPage,
  payouts: PayoutsPage,
  members: MembersPage,
  credentials: CredentialsPage,
  webhook: WebhookPage,
  profile: ProfilePage,
};

function readHash(): string {
  const raw = window.location.hash.replace(/^#\/?/, '');
  return raw === '' ? defaultPath() : raw;
}

export function App() {
  const boot = useBootstrap();
  const [route, setRoute] = useState(readHash);

  useEffect(() => {
    const onHashChange = (): void => { setRoute(readHash()); };
    window.addEventListener('hashchange', onHashChange);
    return () => { window.removeEventListener('hashchange', onHashChange); };
  }, []);

  // 身份变化（登录、切组织）后，若当前路由已不在可见集合内，退回总览
  useEffect(() => {
    if (boot.me === null) return;
    const allowed = visibleNav(boot.me.org.type, boot.me.role, boot.me.channel_scope !== null);
    if (!allowed.some((item) => item.path === route)) {
      window.location.hash = `#/${defaultPath()}`;
    }
  }, [boot.me, route]);

  if (boot.loading) {
    return <div className="login-page"><Loading label="正在恢复会话" /></div>;
  }

  if (boot.me === null) {
    return <LoginPage onSignedIn={boot.reload} />;
  }

  const allowed = visibleNav(boot.me.org.type, boot.me.role, boot.me.channel_scope !== null);
  const active = allowed.find((item) => item.path === route);
  const Page = active === undefined ? null : PAGES[active.path];

  return (
    <SessionProvider me={boot.me} onReload={boot.reload} onLogout={boot.logout}>
      <Shell
        route={route}
        title={active?.label ?? '控制台'}
        onNavigate={(path) => { window.location.hash = `#/${path}`; }}
      >
        {Page === null || Page === undefined ? <Loading label="正在跳转" /> : <Page />}
      </Shell>
    </SessionProvider>
  );
}
